import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Crown, Sparkles, Shield, Heart, Users, Zap } from "lucide-react";

interface SubscriptionDialogProps {
  children: React.ReactNode;
}

export const SubscriptionDialog = ({ children }: SubscriptionDialogProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      console.log('Starting subscription process...');
      
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to join Positivio Club.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('User authenticated, calling create-checkout...');
      const { data, error } = await supabase.functions.invoke('create-checkout');
      console.log('Create-checkout response:', { data, error });
      
      if (error) throw error;
      
      if (data?.url) {
        console.log('Redirecting to Stripe:', data.url);
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: `Failed to start subscription process: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-md mx-auto">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold gradient-text">
            Join Positivio Club
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <Card className="border-primary/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3">
              <Crown className="h-8 w-8 text-yellow-500" />
            </div>
            <CardHeader>
              <CardTitle className="text-xl">Positivio Club</CardTitle>
              <CardDescription>
                <span className="text-2xl font-bold text-primary">$2.99</span>
                <span className="text-muted-foreground">/month</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-green-500" />
                <span className="text-sm">Zero ads forever</span>
              </div>
              <div className="flex items-center gap-3">
                <Heart className="h-4 w-4 text-red-500" />
                <span className="text-sm">No algorithms - chronological feed</span>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="text-sm">Only authentic connections + 3 invites</span>
              </div>
              <div className="flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-purple-500" />
                <span className="text-sm">Zero tracking and profiling</span>
              </div>
              <div className="flex items-center gap-3">
                <Zap className="h-4 w-4 text-yellow-500" />
                <span className="text-sm">Focus on authenticity</span>
              </div>
              
              <div className="pt-4">
                <Badge variant="secondary" className="mb-3">
                  Support independent platform + 3 invitations
                </Badge>
                <p className="text-xs text-muted-foreground">
                  Your subscription helps us maintain Positivio as a space free from 
                  ads, algorithms and social manipulation. Plus you get 3 invitations to share!
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Button 
            onClick={handleSubscribe}
            disabled={loading}
            size="lg"
            className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
          >
            {loading ? "Redirecting..." : "Join Positivio Club"}
          </Button>
          
          <p className="text-xs text-center text-muted-foreground">
            Cancel anytime. Secure payments via Stripe.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};